import React, { useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import utilFunctions from "../../../../utilities/utilFunctions";

// pure.white inverts to black in dark mode in this theme, so use a fixed, slightly softened white
// for badge text that always sits on a blue background.
const BADGE_TEXT_WHITE = "rgba(255, 255, 255, 0.88)";

// Inline dropdown for the Serving Sizes column. The family's defaults are listed first under
// "Product family presets" so they read as the expected set for this product, with every other
// serving size in the establishment below. Each toggle is persisted immediately by the parent.
const ServingSizeSelectColumn = ({
  servingSizes,
  familyServingSizes,
  selectedServingSizes,
  onToggleServingSize,
  disabled,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const selectedIds = new Set((selectedServingSizes || []).map((x) => x.id));
  const familyIds = new Set((familyServingSizes || []).map((x) => x.id));

  const allSizes = servingSizes || [];
  const presets = allSizes.filter((x) => familyIds.has(x.id));
  const otherSizes = allSizes.filter((x) => !familyIds.has(x.id));

  const handleOpen = (event) => {
    if (disabled) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const hasSelection = selectedServingSizes && selectedServingSizes.length > 0;
  const remainingCount = hasSelection ? selectedServingSizes.length - 1 : 0;

  const renderGroupHeader = (label) => (
    <ListSubheader
      disableSticky
      sx={{
        lineHeight: "24px",
        paddingTop: 2,
        paddingBottom: 1,
        backgroundColor: "transparent",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: (theme) => theme.palette.terrain["02"],
      }}
    >
      {label}
    </ListSubheader>
  );

  const renderOption = (servingSize) => {
    const checked = selectedIds.has(servingSize.id);

    return (
      <MenuItem
        key={servingSize.id}
        dense
        onClick={() => onToggleServingSize(servingSize, !checked)}
        sx={{ alignItems: "flex-start" }}
      >
        <Checkbox
          checked={checked}
          size="small"
          sx={{ padding: 0, marginRight: 2, marginTop: 1 }}
        />
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body1">{servingSize.name}</Typography>
          <Typography
            variant="body2"
            sx={{ color: (theme) => theme.palette.terrain["02"] }}
          >
            {utilFunctions.extractServingSizeDisplay(servingSize)}
          </Typography>
        </Box>
      </MenuItem>
    );
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.5 : 1,
          paddingLeft: 2,
          paddingRight: 1,
          width: "100%",
        }}
      >
        {hasSelection ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              overflow: "hidden",
            }}
          >
            <Chip
              size="small"
              label={utilFunctions.extractServingSizeDisplay(
                selectedServingSizes[0]
              )}
              sx={{
                maxWidth: "110px",
                fontWeight: 600,
                borderRadius: "6px",
                backgroundColor: (theme) => theme.palette.info[600],
                color: BADGE_TEXT_WHITE,
                "& .MuiChip-label": { color: BADGE_TEXT_WHITE },
              }}
            />
            {remainingCount > 0 ? (
              <Chip
                size="small"
                label={`+${remainingCount}`}
                sx={{
                  fontWeight: 700,
                  borderRadius: "6px",
                  backgroundColor: (theme) => theme.palette.info[800],
                  color: BADGE_TEXT_WHITE,
                  "& .MuiChip-label": { color: BADGE_TEXT_WHITE },
                }}
              />
            ) : null}
          </Box>
        ) : (
          <Typography
            variant="body1"
            noWrap
            sx={{ color: (theme) => theme.palette.terrain[400] }}
          >
            Assign serving sizes
          </Typography>
        )}
        {open ? (
          <KeyboardArrowUpOutlinedIcon
            sx={{ fontSize: 18, color: (theme) => theme.palette.primary[800] }}
          />
        ) : (
          <KeyboardArrowDownOutlinedIcon
            sx={{ fontSize: 18, color: (theme) => theme.palette.primary[800] }}
          />
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { maxHeight: "420px" } }}
      >
        {allSizes.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body1">No serving sizes found</Typography>
          </MenuItem>
        ) : null}
        {/* the headers only earn their place when there are two groups to tell apart - with no
            family defaults it is just one flat list */}
        {presets.length > 0 ? renderGroupHeader("Product family presets") : null}
        {presets.map(renderOption)}
        {presets.length > 0 && otherSizes.length > 0
          ? renderGroupHeader("Other sizes")
          : null}
        {otherSizes.map(renderOption)}
      </Menu>
    </>
  );
};

export default ServingSizeSelectColumn;
